import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ActivitiesService } from '@/api';
import { presentTodayKeys } from './usePresentToday';
import { activityKeys } from './useActivities';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react';
import type { ListActivitiesResponse, ActivityResponse } from '@/types/activities';

/**
 * Hook to delete an activity with optimistic updates.
 *
 * @returns TanStack Query mutation for deleting an activity
 *
 * @example
 * const deleteActivity = useDeleteActivity();
 * deleteActivity.mutate('activity-123');
 */
export function useDeleteActivity() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (id: string) => ActivitiesService.deleteApiV1Activities(id),
    // Optimistic update: remove from cache immediately
    onMutate: async (id: string) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: activityKeys.lists() });

      // Snapshot the previous value for rollback
      const previousActivities = queryClient.getQueriesData({
        queryKey: activityKeys.lists(),
      });

      // Optimistically remove the activity from all list queries
      queryClient.setQueriesData<ListActivitiesResponse>(
        { queryKey: activityKeys.lists() },
        (old) => {
          if (!old?.items) return old;
          return {
            ...old,
            items: old.items.filter(
              (activity: ActivityResponse) => activity.id !== id
            ),
            total: (old.total ?? 0) - 1,
          };
        }
      );

      // Return context with previous data for rollback
      return { previousActivities };
    },
    onError: (_error, _id, context) => {
      // Rollback on error
      if (context?.previousActivities) {
        context.previousActivities.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      toast.error(
        i18n._({
          id: 'activities.delete.error.title',
          message: 'Failed to delete activity',
        })
      );
    },
    onSuccess: () => {
      toast.success(
        i18n._({
          id: 'activities.delete.success.title',
          message: 'Activity deleted',
        })
      );
    },
    onSettled: () => {
      // Invalidate queries to refetch after mutation settles
      queryClient.invalidateQueries({ queryKey: activityKeys.lists() });
      queryClient.invalidateQueries({ queryKey: presentTodayKeys.all });
    },
  });
}
